export const LINE_FAMILY_META = {
  "aurora-one": {
    familyCode: "AUR",
    familyName: "AURORA ONE",
  },
  "ultra-sheer": {
    familyCode: "US",
    familyName: "ULTRA-SHEER",
  },
  "ultra-flex": {
    familyCode: "UF",
    familyName: "ULTRA-FLEX",
  },
  prime: {
    familyCode: "PRM",
    familyName: "PRIME",
  },
  "stainless-steel": {
    familyCode: "SS",
    familyName: "STAINLESS STEEL",
  },
} as const;

export const FAMILY_CODE_TO_LINE_SLUG = Object.fromEntries(
  Object.entries(LINE_FAMILY_META).map(([slug, value]) => [value.familyCode, slug])
) as Record<string, keyof typeof LINE_FAMILY_META>;

export function getLineFamilyMeta(lineSlug: string) {
  return LINE_FAMILY_META[lineSlug as keyof typeof LINE_FAMILY_META] ?? null;
}

export function getLineSlugForFamilyCode(familyCode: string | null | undefined) {
  if (!familyCode) {
    return null;
  }

  return FAMILY_CODE_TO_LINE_SLUG[familyCode] ?? null;
}

/**
 * Known line slugs — used for title-fallback parsing.
 * Must stay in sync with KNOWN_LINES in shopify-catalog.ts.
 */
const KNOWN_LINE_SLUGS = [
  "ultra-sheer",
  "ultra-flex",
  "aurora-one",
  "stainless-steel",
  "prime",
] as const;

export type AuroraLineSlug = (typeof KNOWN_LINE_SLUGS)[number];

const KNOWN_LINE_NAMES: Record<typeof KNOWN_LINE_SLUGS[number], string> = {
  "aurora-one": "Aurora One",
  "ultra-sheer": "Ultra-Sheer",
  "ultra-flex": "Ultra-Flex",
  "prime": "Prime",
  "stainless-steel": "Stainless Steel",
};

function localSlugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Resolve the product-line slug for a raw Shopify product.
 * Used by UI components (ProductCard, etc.) that need to
 * pick a `.bg-{slug}` class without paying for full
 * parseStringProduct().
 */
export function resolveLineSlugForProduct(product: {
  title: string;
  metafields: readonly unknown[];
}): AuroraLineSlug | null {
  const field = product.metafields.find(
    (f): f is { key: string; value: string | null } => {
      if (!f || typeof f !== "object") return false;
      const m = f as { key?: unknown; value?: unknown };
      return typeof m.key === "string";
    },
  );
  const meta = field?.value ?? null;
  if (meta) {
    const slug = localSlugify(meta);
    if ((KNOWN_LINE_SLUGS as readonly string[]).includes(slug)) {
      return slug as AuroraLineSlug;
    }
  }
  const upper = product.title.toUpperCase();
  for (const slug of KNOWN_LINE_SLUGS) {
    if (upper.startsWith(KNOWN_LINE_NAMES[slug].toUpperCase())) {
      return slug;
    }
  }
  return null;
}

export function auroraLineBgClass(slug: string | null | undefined): string {
  if (!slug) return "";
  return (KNOWN_LINE_SLUGS as readonly string[]).includes(slug)
    ? `bg-${slug}`
    : "";
}

/**
 * Class exposing the line's accent as `--line-accent`, for a restrained badge
 * or rule on a neutral working surface. This is the treatment to use where the
 * customer is working (the configurator), rather than the full-bleed `bg-*`
 * backgrounds, which belong on cards and the line picker.
 */
export function auroraLineAccentClass(slug: string | null | undefined): string {
  return slug && (KNOWN_LINE_SLUGS as readonly string[]).includes(slug)
    ? `line-accent line-accent-${slug}`
    : "line-accent";
}

/**
 * Resolve line slug from a human-readable line name
 * (e.g. "Aurora One", "Stainless Steel"). Case-insensitive.
 * Returns null if no match.
 */
export function resolveLineSlugForName(
  name: string | null | undefined,
): AuroraLineSlug | null {
  if (!name) return null;
  const upper = name.trim().toUpperCase();
  for (const slug of KNOWN_LINE_SLUGS) {
    if (KNOWN_LINE_NAMES[slug].toUpperCase() === upper) return slug;
  }
  const slug = localSlugify(name);
  return (KNOWN_LINE_SLUGS as readonly string[]).includes(slug)
    ? (slug as AuroraLineSlug)
    : null;
}
